import React, { useState, useEffect, useCallback } from 'react';
import {
  getAdvertisements, getAdStats, createAdvertisement, updateAdvertisement,
  updateAdStatus, deleteAdvertisement, getPlatformReligions, getOperationalCities,
} from '../services/adminService';
import {
  pageContainer, cardStyle, statsGrid, labelStyle, valueStyle,
  subTextStyle, thStyle, tdStyle, badgeStyle, tableSearch, smallSelect,
  primaryBtn, secondaryBtn, inputStyle,
} from '../styles/theme';
import { useDebounce, useToast } from '../utils/useAdminPage';
import Paginator from '../components/Paginator';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'grid', placeItems: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { ...cardStyle, width: 560, maxHeight: '85vh', overflow: 'auto' };
const row: React.CSSProperties = { marginBottom: 14 };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: 4 };
const delBtn: React.CSSProperties = { ...secondaryBtn, color: '#ef4444', borderColor: '#fecaca', fontSize: 12, padding: '6px 14px' };

const AD_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ENDED'];
const AD_PLACEMENTS = ['HOMEPAGE_BANNER', 'SCHOLAR_LIST', 'BOOKING_FLOW', 'IN_APP_POPUP', 'EMAIL_CAMPAIGN'];

const Advertisements = () => {
  const [ads, setAds]         = useState<any[]>([]);
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [filterReligion, setFilterReligion] = useState('');
  const [page, setPage]       = useState(1);
  const [religions, setReligions] = useState<string[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving]   = useState(false);
  const pageSize = 10;

  /* form fields */
  const [fName, setFName] = useState('');
  const [fDesc, setFDesc] = useState('');
  const [fBanner, setFBanner] = useState('');
  const [fRedirect, setFRedirect] = useState('');
  const [fReligion, setFReligion] = useState('');
  const [fTargetAudience, setFTargetAudience] = useState('ALL');
  const [fPlacement, setFPlacement] = useState('HOMEPAGE_BANNER');
  const [fStatus, setFStatus] = useState('DRAFT');
  const [fBudget, setFBudget] = useState('');
  const [fStart, setFStart] = useState('');
  const [fEnd, setFEnd] = useState('');
  const [fCity, setFCity] = useState('');
  const [fState, setFState] = useState('');
  const [operationalCities, setOperationalCities] = useState<any[]>([]);

  const dSearch = useDebounce(search, 350);
  const { toast, showToast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [data, statsData, rels, citiesData] = await Promise.all([
      getAdvertisements({ search: dSearch, status, religion: filterReligion }),
      getAdStats(),
      getPlatformReligions(),
      getOperationalCities(),
    ]);
    setAds(data || []);
    if (statsData) setStats(statsData);
    setReligions(rels);
    setOperationalCities(citiesData || []);
    setLoading(false);
  }, [dSearch, status, filterReligion]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const paged = ads.slice((page - 1) * pageSize, page * pageSize);

  const openCreate = () => {
    setEditing(null); setFName(''); setFDesc(''); setFBanner(''); setFRedirect('');
    setFReligion(''); setFTargetAudience('ALL'); setFPlacement('HOMEPAGE_BANNER'); setFStatus('DRAFT');
    setFBudget(''); setFStart(''); setFEnd(''); setFCity(''); setFState('');
    setShowModal(true);
  };

  const openEdit = (ad: any) => {
    setEditing(ad);
    setFName(ad.name || ''); setFDesc(ad.description || ''); setFBanner(ad.bannerUrl || '');
    setFRedirect(ad.redirectUrl || ''); setFReligion(ad.religion || ''); setFTargetAudience(ad.targetAudience || 'ALL');
    setFPlacement(ad.placement || 'HOMEPAGE_BANNER'); setFStatus(ad.status || 'DRAFT');
    setFBudget(ad.budget?.toString() || '');
    setFStart(ad.startDate ? ad.startDate.substring(0, 10) : '');
    setFEnd(ad.endDate ? ad.endDate.substring(0, 10) : '');
    setFCity(ad.targetCity || ''); setFState(ad.targetState || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: fName, description: fDesc, bannerUrl: fBanner, redirectUrl: fRedirect,
        religion: fReligion || null, targetAudience: fTargetAudience, placement: fPlacement, status: fStatus, budget: fBudget ? parseFloat(fBudget) : null,
        startDate: fStart || null, endDate: fEnd || null,
        targetCity: fCity || null, targetState: fState || null,
      };
      editing ? await updateAdvertisement(editing.id, payload) : await createAdvertisement(payload);
      showToast(editing ? 'Advertisement updated' : 'Advertisement created');
      setShowModal(false); fetchData();
    } catch { showToast('Save failed', 'error'); }
    setSaving(false);
  };

  const handleStatusToggle = async (ad: any) => {
    const next = ad.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      await updateAdStatus(ad.id, next);
      showToast(`Ad ${next.toLowerCase()}`); fetchData();
    } catch { showToast('Status update failed', 'error'); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this advertisement?')) return;
    try { await deleteAdvertisement(id); showToast('Advertisement deleted'); fetchData(); }
    catch { showToast('Delete failed', 'error'); }
  };

  const statusColor = (st: string) => {
    switch (st) {
      case 'ACTIVE': return { bg: '#dcfce7', fg: '#15803d' };
      case 'PAUSED': return { bg: '#fef3c7', fg: '#b45309' };
      case 'ENDED':  return { bg: '#fee2e2', fg: '#b91c1c' };
      default:       return { bg: '#f1f5f9', fg: '#64748b' };
    }
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard › Advertisements" title="Advertisement Manager"
        subtitle="Create and manage targeted ad campaigns across the platform."
        onRefresh={fetchData}
        extraButtons={<button style={primaryBtn} onClick={openCreate}>+ New Campaign</button>}
      />

      {/* Stats */}
      {stats && (
        <>
          <div style={{ ...statsGrid, marginBottom: 20 }}>
            <div style={cardStyle}><div style={labelStyle}>Active Campaigns</div><div style={valueStyle}>{stats.activeCampaigns || '0'}</div><div style={subTextStyle}>Currently running</div></div>
            <div style={cardStyle}><div style={labelStyle}>Total Impressions</div><div style={valueStyle}>{(stats.totalImpressions || 0).toLocaleString()}</div><div style={subTextStyle}>Across all campaigns</div></div>
            <div style={cardStyle}><div style={labelStyle}>Total Clicks</div><div style={valueStyle}>{(stats.totalClicks || 0).toLocaleString()}</div><div style={subTextStyle}>Recorded clicks</div></div>
            <div style={cardStyle}><div style={labelStyle}>Avg CTR</div><div style={valueStyle}>{stats.avgCTR || '0'}%</div><div style={subTextStyle}>Click-through rate</div></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div style={cardStyle}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'hsl(var(--foreground))' }}>Religion-wise Distribution</h4>
              {(stats.byReligion || []).length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '10px 0' }}>No religion data</div>
              ) : (
                (stats.byReligion || []).map((item: any, i: number) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <span style={{ color: '#475569', fontWeight: 600 }}>{item.religion}</span>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{item.percentage}%</span>
                  </div>
                ))
              )}
            </div>

            <div style={cardStyle}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'hsl(var(--foreground))' }}>Ad Placement Performance</h4>
              {(stats.byPlacement || []).length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '10px 0' }}>No placement data</div>
              ) : (
                (stats.byPlacement || []).map((item: any, i: number) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                    <span style={{ color: '#475569', fontWeight: 600 }}>{item.placement}</span>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{(item.impressions || 0).toLocaleString()}</span>
                  </div>
                ))
              )}
            </div>

            <div style={cardStyle}>
              <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: 'hsl(var(--foreground))' }}>Target App Performance</h4>
              {(stats.byTargetAudience || []).length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '10px 0' }}>No target app data</div>
              ) : (
                (stats.byTargetAudience || []).map((item: any, i: number) => {
                  let label = '🌐 All Apps';
                  if (item.targetAudience === 'CUSTOMER') label = '👤 Customer App';
                  if (item.targetAudience === 'SCHOLAR') label = '🎓 Scholar App';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
                      <span style={{ color: '#475569', fontWeight: 600 }}>{label}</span>
                      <span style={{ fontWeight: 700, color: '#0f172a' }}>{(item.impressions || 0).toLocaleString()} imp ({item.ctr}%)</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

      {/* Table */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search campaigns…" style={{ ...tableSearch, flex: 1, minWidth: 220 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <div style={{ display: 'flex', gap: 12 }}>
            <select style={smallSelect} value={filterReligion} onChange={e => { setFilterReligion(e.target.value); setPage(1); }}>
              <option value="">All Religions</option>
              {religions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select style={smallSelect} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {AD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12, borderBottom: '1px solid #f1f5f9' }}>
              <th style={thStyle}>CAMPAIGN</th><th style={thStyle}>RELIGION</th><th style={thStyle}>TARGET APP</th><th style={thStyle}>STATUS</th>
              <th style={thStyle}>IMPRESSIONS</th><th style={thStyle}>CTR</th><th style={thStyle}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading…</td></tr>
            ) : paged.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>No advertisements found.</td></tr>
            ) : paged.map((ad: any) => {
              const sc = statusColor(ad.status);
              const ctr = ad.impressions > 0 ? ((ad.clicks / ad.impressions) * 100).toFixed(2) : '0';
              const targetAudience = ad.targetAudience || 'ALL';
              let audienceBadge = { bg: 'rgba(16, 185, 129, 0.12)', fg: '#059669', label: '🌐 All Apps' };
              if (targetAudience === 'CUSTOMER') {
                audienceBadge = { bg: 'rgba(37, 99, 235, 0.12)', fg: '#2563eb', label: '👤 Customer App' };
              } else if (targetAudience === 'SCHOLAR') {
                audienceBadge = { bg: 'rgba(168, 85, 247, 0.12)', fg: '#9333ea', label: '🎓 Scholar App' };
              }
              return (
                <tr key={ad.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{ad.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{ad.redirectUrl || 'No URL set'}</div>
                  </td>
                  <td style={tdStyle}><span style={badgeStyle}>{ad.religion || 'ALL'}</span></td>
                  <td style={tdStyle}>
                    <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: audienceBadge.bg, color: audienceBadge.fg }}>
                      {audienceBadge.label}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.fg, cursor: 'pointer' }}
                      onClick={() => handleStatusToggle(ad)} title="Click to toggle">
                      ● {ad.status}
                    </span>
                  </td>
                  <td style={tdStyle}>{(ad.impressions || 0).toLocaleString()}</td>
                  <td style={tdStyle}>{ctr}%</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...secondaryBtn, fontSize: 12, padding: '6px 14px' }} onClick={() => openEdit(ad)}>Edit</button>
                      <button style={delBtn} onClick={() => handleDelete(ad.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginator total={ads.length} page={page} pageSize={pageSize} onPage={setPage} />
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'hsl(var(--foreground))' }}>
              {editing ? 'Edit Campaign' : 'New Campaign'}
            </h3>

            <div style={row}><label style={lbl}>Campaign Name *</label><input style={inputStyle} value={fName} onChange={e => setFName(e.target.value)} /></div>
            <div style={row}><label style={lbl}>Description</label><input style={inputStyle} value={fDesc} onChange={e => setFDesc(e.target.value)} /></div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={row}><label style={lbl}>Banner URL</label><input style={inputStyle} value={fBanner} onChange={e => setFBanner(e.target.value)} /></div>
              <div style={row}><label style={lbl}>Redirect URL</label><input style={inputStyle} value={fRedirect} onChange={e => setFRedirect(e.target.value)} /></div>
            </div>

            <div style={row}>
              <label style={lbl}>Target App / Audience *</label>
              <select style={inputStyle} value={fTargetAudience} onChange={e => setFTargetAudience(e.target.value)}>
                <option value="ALL">ALL (User & Scholar Apps)</option>
                <option value="CUSTOMER">CUSTOMER (User App Only)</option>
                <option value="SCHOLAR">SCHOLAR (Scholar App Only)</option>
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div style={row}>
                <label style={lbl}>Religion</label>
                <select style={inputStyle} value={fReligion} onChange={e => setFReligion(e.target.value)}>
                  <option value="">All</option>
                  {religions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={row}>
                <label style={lbl}>Placement</label>
                <select style={inputStyle} value={fPlacement} onChange={e => setFPlacement(e.target.value)}>
                  {AD_PLACEMENTS.map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div style={row}>
                <label style={lbl}>Status</label>
                <select style={inputStyle} value={fStatus} onChange={e => setFStatus(e.target.value)}>
                  {AD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={row}><label style={lbl}>Start Date</label><input type="date" style={inputStyle} value={fStart} onChange={e => setFStart(e.target.value)} /></div>
              <div style={row}><label style={lbl}>End Date</label><input type="date" style={inputStyle} value={fEnd} onChange={e => setFEnd(e.target.value)} /></div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={row}>
                <label style={lbl}>Target City</label>
                <select
                  style={inputStyle}
                  value={fCity}
                  onChange={e => {
                    const val = e.target.value;
                    setFCity(val);
                    if (val) {
                      const matched = operationalCities.find(c => c.name.toLowerCase() === val.toLowerCase());
                      if (matched?.state) {
                        setFState(matched.state);
                      }
                    }
                  }}
                >
                  <option value="">All Cities</option>
                  {operationalCities.map(c => (
                    <option key={c.id} value={c.name}>{c.name} ({c.state})</option>
                  ))}
                </select>
              </div>
              <div style={row}>
                <label style={lbl}>Target State</label>
                <select
                  style={inputStyle}
                  value={fState}
                  onChange={e => setFState(e.target.value)}
                >
                  <option value="">All States</option>
                  {Array.from(new Set(operationalCities.map(c => c.state).filter(Boolean))).map((s: any) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={secondaryBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={primaryBtn} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Advertisements;
